import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface AutomationRule {
  id: string;
  name: string;
  description: string;
  type: 'offer_expiry' | 'offer_status' | 'client_activity' | 'scheduled';
  trigger: any;
  action: any;
  is_active: boolean;
  last_triggered?: string;
  execution_count?: number;
  created_at: string;
  updated_at?: string;
}

interface NotificationSettings {
  email: boolean;
  push: boolean;
  inApp: boolean;
  digest: 'instant' | 'daily' | 'weekly';
}

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  subject: string;
  body: string;
  variables: string[] | any; // Obsługa JSONB z Supabase
  category?: string;
  is_active?: boolean;
}

export const useAutomation = () => {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    email: true,
    push: false,
    inApp: true,
    digest: 'instant'
  });
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pobierz reguły automatyzacji
  const fetchRules = async () => {
    try {
      const { data, error } = await supabase
        .from('automation_rules')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRules(data || []);
    } catch (err: any) {
      console.error('Error fetching automation rules:', err);
      setError(err.message);
    }
  };

  // Pobierz ustawienia powiadomień
  const fetchNotificationSettings = async () => {
    try {
      // Użyj stałego UUID dla domyślnego użytkownika
      const userId = '00000000-0000-0000-0000-000000000001';
      
      // Najpierw sprawdź czy istnieje rekord
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data?.notification_settings) {
        setNotificationSettings(data.notification_settings);
      } else if (!data) {
        // Utwórz domyślny rekord
        const defaultSettings = {
          email: true,
          push: false,
          inApp: true,
          digest: 'instant'
        };
        
        await supabase
          .from('user_settings')
          .insert({
            user_id: userId,
            notification_settings: defaultSettings
          });
      }
    } catch (err: any) {
      console.error('Error fetching notification settings:', err);
    }
  };

  // Pobierz szablony email
  const fetchEmailTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      // Konwersja JSONB variables na array jeśli potrzeba
      const templates = (data || []).map(template => ({
        ...template,
        variables: Array.isArray(template.variables) 
          ? template.variables 
          : (typeof template.variables === 'string' 
              ? JSON.parse(template.variables) 
              : template.variables || [])
      }));
      setEmailTemplates(templates);
    } catch (err: any) {
      console.error('Error fetching email templates:', err);
      setError(err.message);
    }
  };

  // Załaduj wszystkie dane przy starcie
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchRules(),
        fetchNotificationSettings(),
        fetchEmailTemplates()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Dodaj/aktualizuj regułę
  const saveRule = async (rule: Partial<AutomationRule>) => {
    try {
      if (rule.id) {
        // Aktualizacja
        const { data, error } = await supabase
          .from('automation_rules')
          .update({
            name: rule.name,
            description: rule.description,
            type: rule.type,
            trigger: rule.trigger,
            action: rule.action,
            is_active: rule.is_active
          })
          .eq('id', rule.id)
          .select()
          .single();

        if (error) throw error;
        
        setRules(prev => prev.map(r => r.id === data.id ? data : r));
        return { success: true, data };
      } else {
        // Nowa reguła
        const { data, error } = await supabase
          .from('automation_rules')
          .insert({
            name: rule.name,
            description: rule.description,
            type: rule.type,
            trigger: rule.trigger,
            action: rule.action,
            is_active: rule.is_active || true
          })
          .select()
          .single();

        if (error) throw error;
        
        setRules(prev => [...prev, data]);
        return { success: true, data };
      }
    } catch (err: any) {
      console.error('Error saving rule:', err);
      return { success: false, error: err.message };
    }
  };

  // Przełącz aktywność reguły
  const toggleRule = async (ruleId: string) => {
    try {
      const rule = rules.find(r => r.id === ruleId);
      if (!rule) return;

      const { data, error } = await supabase
        .from('automation_rules')
        .update({ is_active: !rule.is_active })
        .eq('id', ruleId)
        .select()
        .single();

      if (error) throw error;
      
      setRules(prev => prev.map(r => r.id === ruleId ? data : r));
      return { success: true };
    } catch (err: any) {
      console.error('Error toggling rule:', err);
      return { success: false, error: err.message };
    }
  };

  // Usuń regułę
  const deleteRule = async (ruleId: string) => {
    try {
      const { error } = await supabase
        .from('automation_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;
      
      setRules(prev => prev.filter(r => r.id !== ruleId));
      return { success: true };
    } catch (err: any) {
      console.error('Error deleting rule:', err);
      return { success: false, error: err.message };
    }
  };

  // Zapisz ustawienia powiadomień
  const saveNotificationSettings = async (settings: NotificationSettings) => {
    try {
      const userId = '00000000-0000-0000-0000-000000000001'; // Stały UUID dla domyślnego użytkownika
      
      // Najpierw sprawdź czy rekord istnieje
      const { data: existing } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      let error;
      
      if (existing) {
        // Rekord istnieje - użyj update
        const result = await supabase
          .from('user_settings')
          .update({
            notification_settings: settings,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        error = result.error;
      } else {
        // Rekord nie istnieje - użyj insert
        const result = await supabase
          .from('user_settings')
          .insert({
            user_id: userId,
            notification_settings: settings
          });
        error = result.error;
      }

      if (error) throw error;
      
      setNotificationSettings(settings);
      return { success: true };
    } catch (err: any) {
      console.error('Error saving notification settings:', err);
      return { success: false, error: err.message };
    }
  };

  // Zapisz szablon email
  const saveEmailTemplate = async (template: EmailTemplate) => {
    try {
      if (template.id) {
        // Aktualizacja
        const { data, error } = await supabase
          .from('email_templates')
          .update({
            name: template.name,
            subject: template.subject,
            body: template.body,
            variables: template.variables
          })
          .eq('id', template.id)
          .select()
          .single();

        if (error) throw error;
        
        setEmailTemplates(prev => prev.map(t => t.id === data.id ? data : t));
        return { success: true, data };
      } else {
        // Nowy szablon
        const { data, error } = await supabase
          .from('email_templates')
          .insert({
            code: template.code,
            name: template.name,
            subject: template.subject,
            body: template.body,
            variables: template.variables,
            category: template.category,
            is_active: true
          })
          .select()
          .single();

        if (error) throw error;
        
        setEmailTemplates(prev => [...prev, data]);
        return { success: true, data };
      }
    } catch (err: any) {
      console.error('Error saving email template:', err);
      return { success: false, error: err.message };
    }
  };

  // Zapisz wykonanie reguły
  const logExecution = async (ruleId: string, status: 'success' | 'failed', errorMessage?: string) => {
    try {
      const { error } = await supabase
        .from('automation_executions')
        .insert({
          rule_id: ruleId,
          status,
          error_message: errorMessage,
          executed_at: new Date().toISOString()
        });

      if (error) throw error;

      // Aktualizuj licznik wykonań i ostatnie wykonanie
      await supabase
        .from('automation_rules')
        .update({ 
          last_triggered: new Date().toISOString(),
          execution_count: rules.find(r => r.id === ruleId)?.execution_count || 0 + 1
        })
        .eq('id', ruleId);

      return { success: true };
    } catch (err: any) {
      console.error('Error logging execution:', err);
      return { success: false, error: err.message };
    }
  };

  // Pobierz historię wykonań
  const getExecutionHistory = async (ruleId?: string) => {
    try {
      let query = supabase
        .from('automation_executions')
        .select('*, rule:automation_rules(name)')
        .order('executed_at', { ascending: false })
        .limit(100);

      if (ruleId) {
        query = query.eq('rule_id', ruleId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { success: true, data };
    } catch (err: any) {
      console.error('Error fetching execution history:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    rules,
    notificationSettings,
    emailTemplates,
    loading,
    error,
    saveRule,
    toggleRule,
    deleteRule,
    saveNotificationSettings,
    saveEmailTemplate,
    logExecution,
    getExecutionHistory,
    refreshData: async () => {
      await Promise.all([
        fetchRules(),
        fetchNotificationSettings(),
        fetchEmailTemplates()
      ]);
    }
  };
};
